import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useEffect } from "react";

import useCreateNewEntity from "@/hooks/useCreateNewEntity";
import useUpdateEntity from "@/hooks/useUpdateEntity";
import { RootState } from "@/store/store";
import AppointmentsMainTimeSelector from "../AppointmentsMainTimeSelector/AppointmentsMainTimeSelector";
import InputFieldCalendar from "@/components/InputFieldCalendar/InputFieldCalendar";
import BlockTotalInfo from "../AppointmentsTotalInfo/BlockTotalInfo";
import { setAppointemtId } from "@/store/schedule/scheduleSlice";
import convertToMinutes from "@/utils/converteToMinutes";
import calculateDuration from "@/utils/calculateDoration";
import { updateSchedule, addAppointment } from "@/store/schedule/scheduleSlice";
import { setIseditAppointment } from "@/store/ui/ui";
import useAppointValidation from "@/hooks/useAppointValidation";
import Button from "@/shared/components/Button/Button";
import Title from "@/shared/components/Title/Title";

import "./AppointmentsMainRightPart.scss";
import { toast } from "react-toastify";
import { visitMessage } from "@/utils/constants";

function AppointmentsMainRightPart() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isEditAppointment = useSelector(
        (state: RootState) => state.ui.isEditAppointment,
    );
    const { startTime, endTime } = useSelector((state: RootState) =>
        isEditAppointment
            ? state.appointments.existtAppointment
            : state.appointments.newAppointment,
    );

    const appointmentData = useSelector(
        (state: RootState) => state.appointments,
    );
    const appointemntId = useSelector(
        (state: RootState) => state.schedule.appointmentId,
    );
    const isLocalLoading = useSelector(
        (state: RootState) => state.ui.isLocalLoading,
    );
    const isAppointmentComplete = useSelector(
        (state: RootState) =>
            state.appointments.existtAppointment.isAppointmentComplete,
    );
    const { validate, resetValidation } = useAppointValidation();
    const { handleCreate } = useCreateNewEntity();
    const { handleUpdate } = useUpdateEntity();
    const errors = useSelector((state: RootState) => state.ui.errors);

    async function handleClick(): Promise<void> {
        if (!isAppointmentComplete) {
            const validationErrors = validate();

            if (
                validationErrors.patientName ||
                validationErrors.doctorName ||
                validationErrors.endTime
            ) {
                return;
            }

            if (isEditAppointment) {
                const updateAppointment = {
                    patientId: appointmentData.existtAppointment.patientId._id,
                    doctorId: appointmentData.existtAppointment.doctorId._id,
                    date: appointmentData.existtAppointment.date,
                    chairNumber: appointmentData.existtAppointment.chairNumber,
                    startTime: appointmentData.existtAppointment.startTime,
                    endTime: appointmentData.existtAppointment.endTime,
                    duration: appointmentData.existtAppointment.duration,
                    services: appointmentData.existtAppointment.services,
                    notes: appointmentData.existtAppointment.notes,
                    hasVisited: appointmentData.existtAppointment.hasVisited,
                };
                await handleUpdate({
                    entity: "appointments",
                    id: appointemntId,
                    data: updateAppointment,
                    onSuccess: onSuccessUpdate,
                });
            } else {
                const createAppointment = {
                    patientId: appointmentData.newAppointment.patientId,
                    doctorId: appointmentData.newAppointment.doctorId,
                    date: appointmentData.newAppointment.date,
                    chairNumber: appointmentData.newAppointment.chairNumber,
                    startTime: appointmentData.newAppointment.startTime,
                    endTime: appointmentData.newAppointment.endTime,
                    duration: appointmentData.newAppointment.duration,
                    services: appointmentData.newAppointment.services,
                    notes: appointmentData.newAppointment.notes,
                    paymentHistory:
                        appointmentData.newAppointment.paymentHistory,
                    isAppointmentComplete: false,
                    hasVisited: true,
                };

                await handleCreate({
                    entity: "appointments",
                    data: createAppointment,
                    message: "Візит пацієнта успішно створено",
                    onSuccess: onSuccessCreate,
                });
            }
            resetValidation();
        } else toast.error(visitMessage);
    }

    function onSuccessCreate() {
        dispatch(addAppointment(appointmentData.newAppointment));
        navigate("/schedule");
    }

    function onSuccessUpdate() {
        dispatch(updateSchedule(appointmentData.existtAppointment));
        dispatch(setIseditAppointment(false));
        dispatch(setAppointemtId(""));
        navigate("/schedule");
    }

    useEffect(() => {
        return () => {
            resetValidation();
        };
    }, []);

    return (
        <section className='appointment_right_part appointment_part'>
            <Title tag='h3' text={"Дата і час"} />
            <InputFieldCalendar
                text={!isEditAppointment ? "Поточна дата" : "Дата візиту"}
            />
            <div className='appointment_right_part_time_selector_container'>
                <AppointmentsMainTimeSelector flag={"start"} />
                <AppointmentsMainTimeSelector flag={"end"} />
            </div>
            {errors.startTime && startTime?.length === 0 && (
                <p className='error_message'>{errors.startTime.message}</p>
            )}
            {errors?.endTime && endTime?.length === 0 && (
                <p className='error_message'>{errors.endTime.message}</p>
            )}
            <p
                className={`visit_duration ${
                    startTime &&
                    endTime &&
                    convertToMinutes(endTime) > convertToMinutes(startTime)
                        ? null
                        : "unvisible"
                }`}
            >
                Тривалість &nbsp;{"-"}&nbsp;
                {startTime &&
                endTime &&
                convertToMinutes(endTime) > convertToMinutes(startTime)
                    ? calculateDuration(startTime, endTime)
                    : null}
                &nbsp; хвилин
            </p>
            <BlockTotalInfo />
            <Button
                text={isEditAppointment ? "Зберегти зміни" : "Додати запис"}
                variant='approved'
                type='button'
                onClick={handleClick}
                isLoading={isLocalLoading}
            />
        </section>
    );
}

export default React.memo(AppointmentsMainRightPart);
